import type { Actor, Direction } from '@tama/game-core';
import { TILE, standingPoint } from './layout';

/**
 * The bridge between the grid and the screen.
 *
 * The rules move the pet a whole tile at a time; a pet that teleported between
 * tiles would look like a bug. This module keeps a pixel position that chases
 * the authoritative tile position at a fixed speed, and decides which pose the
 * cat is in on the way. It is pure — no Pixi, no clock — so the walk cycle is
 * testable without a canvas.
 */

export type CatAnim = 'sit' | 'walk' | 'sleep' | 'hop';

export interface Walker {
  /** Design-space pixel position of the pet's feet. */
  x: number;
  y: number;
  facing: Direction;
  anim: CatAnim;
  /** Seconds spent in the current pose — drives the animation cycles. */
  animTime: number;
  /** Seconds during which a reaction pose outranks walking or idling. */
  reactionFor: number;
}

/** Tiles per second. Fast enough that a held key feels responsive, slow enough to see the legs move. */
export const WALK_SPEED = 5.6;

/** Beyond this, the pet is not walking, it has been moved — so it cuts rather than slides. */
const TELEPORT_DISTANCE = TILE * 2.5;

/** Under this many pixels the pet counts as arrived; smaller and it jitters on the spot. */
const ARRIVED = 0.5;

export function createWalker(actor: Actor): Walker {
  const { x, y } = standingPoint(actor.x, actor.y);
  return { x, y, facing: actor.facing, anim: 'sit', animTime: 0, reactionFor: 0 };
}

/**
 * Advances the walker one frame towards where the rules say the pet is.
 *
 * `dtSeconds` is clamped, because a backgrounded tab hands back a delta of
 * several seconds on resume and the pet would appear to have been fired across
 * the room.
 */
export function stepWalker(walker: Walker, actor: Actor, dtSeconds: number): Walker {
  const dt = Math.min(Math.max(dtSeconds, 0), 0.1);
  const target = standingPoint(actor.x, actor.y);

  const dx = target.x - walker.x;
  const dy = target.y - walker.y;
  const distance = Math.hypot(dx, dy);

  let { x, y } = walker;
  if (distance > TELEPORT_DISTANCE) {
    x = target.x;
    y = target.y;
  } else if (distance > ARRIVED) {
    const step = Math.min(distance, WALK_SPEED * TILE * dt);
    x += (dx / distance) * step;
    y += (dy / distance) * step;
  } else {
    x = target.x;
    y = target.y;
  }

  const reactionFor = Math.max(0, walker.reactionFor - dt);
  const moving = Math.hypot(target.x - x, target.y - y) > ARRIVED;
  const anim = nextAnim(walker, moving, reactionFor);

  return {
    x,
    y,
    facing: actor.facing,
    anim,
    // Restarting the clock on a pose change is what stops a walk cycle from
    // beginning mid-stride when the pet sets off again.
    animTime: anim === walker.anim ? walker.animTime + dt : 0,
    reactionFor,
  };
}

function nextAnim(walker: Walker, moving: boolean, reactionFor: number): CatAnim {
  // A reaction holds the pose until it lapses — except that walking away from
  // it cancels it, because the player has clearly moved on.
  if (reactionFor > 0 && !moving) return walker.anim;
  return moving ? 'walk' : 'sit';
}

/** Something happened — hold a pose for a moment instead of going straight back to sitting. */
export function reactWith(walker: Walker, anim: CatAnim, seconds: number): Walker {
  return { ...walker, anim, animTime: 0, reactionFor: seconds };
}

/** Which way to mirror the sprite. The walk cycle is drawn side-on and faces right. */
export function facingScale(facing: Direction): 1 | -1 {
  return facing === 'left' ? -1 : 1;
}
